import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import * as cp from '@certhon/cloud-permissions';
import { AdminAccount } from '@certhon/models';

interface PermissionsGridProps {
  account: AdminAccount;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

// Permissions that should always be disabled in the UI
const ALWAYS_DISABLED = ['QA_SUPER_USER', 'SYS_ADMIN'];

export function PermissionsGrid({
  account,
  value,
  onChange,
  disabled,
}: PermissionsGridProps) {
  const decoded = cp.decode(value);
  const permissionNames = Object.keys(decoded) as Array<keyof typeof decoded>;

  const handleToggle = (permName: string, checked: boolean) => {
    const newDecoded = { ...decoded, [permName]: checked };
    onChange(cp.encode(newDecoded));
  };

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
      {permissionNames.map((permName) => {
        const isChecked = decoded[permName as keyof typeof decoded];
        // Unassignable permissions can still be revoked when already granted
        const isUnassignable =
          !cp.isAssignable(permName, account.internal) && !isChecked;
        const isAlwaysDisabled = ALWAYS_DISABLED.includes(permName);
        const isDisabled = disabled || isAlwaysDisabled || isUnassignable;

        const item = (
          <div key={permName} className="flex items-center space-x-2">
            <Checkbox
              id={`perm-${permName}`}
              checked={isChecked}
              onCheckedChange={(checked) => handleToggle(permName, !!checked)}
              disabled={isDisabled}
            />
            <Label
              htmlFor={`perm-${permName}`}
              className={isDisabled ? 'text-muted-foreground' : ''}
            >
              {permName.replace(/_/g, ' ')}
            </Label>
          </div>
        );

        // Explain why these can't be ticked in edit mode
        return isAlwaysDisabled && !disabled ? (
          <Tooltip key={permName}>
            <TooltipTrigger asChild>{item}</TooltipTrigger>
            <TooltipContent>
              Can only be assigned in the database
            </TooltipContent>
          </Tooltip>
        ) : (
          item
        );
      })}
    </div>
  );
}
